import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';

const DesignPreview = () => {
  const [selectedDesigns, setSelectedDesigns] = useState({
    ourStory: 'option1',
    ourMethod: 'option1',
    team: 'option1'
  });

  const ourStoryOptions = [
    {
      id: 'option1',
      name: 'Modern Cityscape',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=2000',
      overlay: 'gradient-to-r from-spire-purple/90 to-spire-purple/60',
      description: 'Sleek Calgary skyline with purple gradient overlay'
    },
    {
      id: 'option2',
      name: 'Mountain Skyline',
      image: 'https://images.unsplash.com/photo-1519680772-c4b40e40d1a8?q=85&w=2000',
      overlay: 'gradient-to-r from-gray-900/80 to-transparent',
      description: 'Calgary with Rocky Mountains in background'
    },
    {
      id: 'option3',
      name: 'Architectural Excellence',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=85&w=2000',
      overlay: 'gradient-to-br from-spire-purple/85 via-purple-900/70 to-transparent',
      description: 'Modern architecture with dramatic angles'
    },
    {
      id: 'option4',
      name: 'Heritage & Modern',
      image: 'https://images.unsplash.com/photo-1494145904049-0dca59b4bbad?q=85&w=2000',
      overlay: 'gradient-to-r from-spire-purple/90 via-spire-purple/70 to-purple-900/50',
      description: 'Blend of historic and contemporary Calgary buildings'
    }
  ];

  const ourMethodOptions = [
    {
      id: 'option1',
      name: 'Strategic Planning',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=85&w=2000',
      overlay: 'gradient-to-r from-spire-green/90 to-spire-green/60',
      description: 'Business strategy and planning imagery'
    },
    {
      id: 'option2',
      name: 'Collaboration',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=85&w=2000',
      overlay: 'gradient-to-br from-spire-purple/85 to-spire-green/70',
      description: 'Team collaboration and partnership'
    },
    {
      id: 'option3',
      name: 'Blueprint Success',
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=85&w=2000',
      overlay: 'gradient-to-r from-gray-900/85 to-spire-purple/70',
      description: 'Architectural blueprints and planning'
    },
    {
      id: 'option4',
      name: 'Analytics & Data',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=85&w=2000',
      overlay: 'gradient-to-br from-spire-purple/90 via-purple-900/75 to-transparent',
      description: 'Data-driven decision making'
    }
  ];

  const teamOptions = [
    {
      id: 'option1',
      name: 'Calgary Skyline',
      image: 'https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?q=85&w=2000',
      overlay: 'gradient-to-r from-spire-purple/90 to-transparent',
      description: 'Downtown Calgary with Bow River'
    },
    {
      id: 'option2',
      name: 'Modern Office',
      image: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?q=85&w=2000',
      overlay: 'gradient-to-br from-spire-purple/85 to-purple-900/60',
      description: 'Contemporary office space'
    },
    {
      id: 'option3',
      name: 'Team Excellence',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=85&w=2000',
      overlay: 'gradient-to-r from-spire-green/85 to-spire-purple/70',
      description: 'Professional team collaboration'
    },
    {
      id: 'option4',
      name: 'Western Landscape',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=85&w=2000',
      overlay: 'gradient-to-br from-spire-purple/80 via-purple-900/60 to-transparent',
      description: 'Beautiful North America mountains'
    }
  ];

  const selectDesign = (page, optionId) => {
    setSelectedDesigns(prev => ({ ...prev, [page]: optionId }));
  };

  const DesignOption = ({ option, page, isSelected }) => (
    <div 
      onClick={() => selectDesign(page, option.id)}
      className={`relative cursor-pointer group overflow-hidden rounded-2xl transition-all duration-300 ${
        isSelected ? 'ring-4 ring-spire-purple shadow-2xl scale-105' : 'hover:scale-102 shadow-lg'
      }`}
    >
      <div className="relative h-64">
        <img 
          src={option.image} 
          alt={option.name}
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-${option.overlay}`}></div>
        
        {isSelected && (
          <div className="absolute top-4 right-4 w-12 h-12 bg-spire-purple rounded-full flex items-center justify-center shadow-lg">
            <Check className="text-white" size={24} />
          </div>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
          <h3 className="text-white font-bold text-xl mb-1">{option.name}</h3>
          <p className="text-white/90 text-sm">{option.description}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Link to="/" className="inline-block mb-8">
            <img src="/spire-logo.png" alt="Spire Group" className="h-16 mx-auto" />
          </Link>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Design Options Preview</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Review these beautiful design options for your page headers. Select your favorites and we'll implement them on the live site.
          </p>
        </div>

        {/* Our Story Section */}
        <section className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Our Story Page Header</h2>
              <p className="text-gray-600">Choose the hero image for your company story</p>
            </div>
            <Link to="/preview/our-story" className="px-6 py-3 bg-spire-purple text-white rounded-lg font-semibold hover:bg-purple-700 transition">
              Preview Full Page →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {ourStoryOptions.map(option => (
              <DesignOption
                key={option.id}
                option={option}
                page="ourStory"
                isSelected={selectedDesigns.ourStory === option.id}
              />
            ))}
          </div>
        </section>

        {/* Our Method Section */}
        <section className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Our Method Page Header</h2>
              <p className="text-gray-600">Select the visual style for your methodology</p>
            </div>
            <Link to="/preview/our-method" className="px-6 py-3 bg-spire-purple text-white rounded-lg font-semibold hover:bg-purple-700 transition">
              Preview Full Page →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {ourMethodOptions.map(option => (
              <DesignOption
                key={option.id}
                option={option}
                page="ourMethod"
                isSelected={selectedDesigns.ourMethod === option.id}
              />
            ))}
          </div>
        </section>

        {/* Team Section */}
        <section className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Team Page Header</h2>
              <p className="text-gray-600">Pick the backdrop for your team showcase</p>
            </div>
            <Link to="/preview/team" className="px-6 py-3 bg-spire-purple text-white rounded-lg font-semibold hover:bg-purple-700 transition">
              Preview Full Page →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {teamOptions.map(option => (
              <DesignOption
                key={option.id}
                option={option}
                page="team"
                isSelected={selectedDesigns.team === option.id}
              />
            ))}
          </div>
        </section>

        {/* Selection Summary */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Current Selections</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <span className="font-semibold text-gray-900">Our Story:</span>
                <span className="ml-2 text-gray-600">
                  {ourStoryOptions.find(o => o.id === selectedDesigns.ourStory)?.name}
                </span>
              </div>
              <Check className="text-spire-purple" size={20} />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <span className="font-semibold text-gray-900">Our Method:</span>
                <span className="ml-2 text-gray-600">
                  {ourMethodOptions.find(o => o.id === selectedDesigns.ourMethod)?.name}
                </span>
              </div>
              <Check className="text-spire-purple" size={20} />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <span className="font-semibold text-gray-900">Team:</span>
                <span className="ml-2 text-gray-600">
                  {teamOptions.find(o => o.id === selectedDesigns.team)?.name}
                </span>
              </div>
              <Check className="text-spire-purple" size={20} />
            </div>
          </div>
          <div className="mt-8 p-6 bg-spire-purple/10 rounded-xl border-2 border-spire-purple">
            <p className="text-gray-800 text-center">
              <strong>Ready to implement?</strong> Share your selections and I'll update the live site with your chosen designs!
            </p>
          </div>
        </div>

        {/* Back to Site */}
        <div className="text-center mt-12">
          <Link to="/" className="text-spire-purple hover:text-purple-700 font-semibold text-lg">
            ← Back to Main Site
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DesignPreview;